import type { RefObject } from "react"
import { useCallback, useEffect, useRef } from "react"

import { useProductPreferenceAssociationsStore } from "../stores/useProductPreferenceAssociationsStore"

import type { GetCompanyProductAssociationListQuery } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"

export type PreferenceAssociationItem = NonNullable<
  GetCompanyProductAssociationListQuery["companyProductAssociation"]["list"]["items"]
>[number]

interface UseProductPreferenceAssociationListParams {
  companyId: string | undefined
  productId: number
}

interface UseProductPreferenceAssociationListReturn {
  associations: PreferenceAssociationItem[]
  isFetching: boolean
  tableContainerRef: RefObject<HTMLDivElement>
  handleScroll: () => void
  refetch: () => void
}

export const useProductPreferenceAssociationList = ({
  companyId,
  productId,
}: UseProductPreferenceAssociationListParams): UseProductPreferenceAssociationListReturn => {
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const associations = useProductPreferenceAssociationsStore(({ data }) => data)
  const fetching = useProductPreferenceAssociationsStore(({ fetching }) => fetching)
  const executeSearch = useProductPreferenceAssociationsStore(({ executeSearch }) => executeSearch)
  const loadMore = useProductPreferenceAssociationsStore(({ loadMore }) => loadMore)
  const clearStore = useProductPreferenceAssociationsStore(({ clearStore }) => clearStore)

  useEffect(() => () => clearStore(), [clearStore])

  useEffect(() => {
    if (!companyId) return
    executeSearch(queryClient, { companyId, productId })
  }, [executeSearch, companyId, productId])

  const [throttledLoadMore] = useThrottle(() => {
    if (!companyId) return
    loadMore(queryClient, { companyId, productId })
  }, 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const refetch = useCallback(() => {
    if (!companyId) return
    executeSearch(queryClient, { companyId, productId }, true)
  }, [executeSearch, companyId, productId])

  return {
    associations,
    isFetching: fetching,
    tableContainerRef,
    handleScroll,
    refetch,
  }
}
